import { ReactNode, useEffect, useState } from "react";
import ProblemStatement from "./ProblemStatement.js";
import {
  api,
  difficultyLabel,
  type Difficulty,
  type LibraryQuestion,
  type LibraryQuestionDetail,
} from "../api.js";

/** "c" is how the column stores it; "C" is how a teacher reads it. */
export const languageLabel = (l: string) =>
  ({ c: "C", cpp: "C++", java: "Java", python: "Python" })[l] ?? l;

const PAGE_SIZES = [10, 25, 50, 100];

/**
 * Browsing and picking library questions, wherever that is being done.
 *
 * There are three places to pick from the library -- the Library tab, a sheet
 * being built, and an exam paper -- and they differ only in where the questions
 * land. Everything before that point is the same work: search, filter, page,
 * read the hidden tests, tick several, change the filter, tick more. Three
 * copies of it would drift, and the two inside modals would be the ones that
 * rot.
 *
 * The picker owns the browsing and the selection. The caller owns the
 * destination and what an import means, and reports back which source ids
 * landed so the selection can drop them.
 */
export default function LibraryPicker({
  extraFilter,
  onImport,
  importDisabled,
  importLabel = "Import",
  onError,
}: {
  /** Rendered alongside the filters -- the caller's destination control, if any. */
  extraFilter?: ReactNode;
  /** Copies the questions. Returns the source ids that actually landed. */
  onImport: (ids: string[]) => Promise<string[]>;
  importDisabled?: boolean;
  importLabel?: string;
  onError: (message: string) => void;
}) {
  const [questions, setQuestions] = useState<LibraryQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  // Selected question ids, kept across pages and filter changes: picking two
  // easy ones and a hard one means changing the filter in between, and a
  // selection that cleared itself under you would be worse than no selection.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [difficulty, setDifficulty] = useState<Difficulty | "">("");
  const [topic, setTopic] = useState("");
  const [topics, setTopics] = useState<{ topic: string; count: number }[]>([]);
  const [language, setLanguage] = useState("");
  const [languages, setLanguages] = useState<{ language: string; count: number }[]>([]);
  const [importing, setImporting] = useState<string | null>(null);

  // Previews, fetched the first time a question is opened and kept afterwards:
  // comparing two questions means opening both, and re-fetching on every toggle
  // would make that flicker.
  const [openId, setOpenId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, LibraryQuestionDetail>>({});
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);

  const load = (toPage = page, size = pageSize) => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (difficulty) q.set("difficulty", difficulty);
    if (topic) q.set("topic", topic);
    if (language) q.set("language", language);
    q.set("page", String(toPage));
    q.set("pageSize", String(size));
    api
      .get<{ questions: LibraryQuestion[]; total: number }>(`/api/library?${q}`)
      .then((r) => {
        setQuestions(r.questions);
        setTotal(r.total);
      })
      .catch((e) => onError(e.message));
  };

  useEffect(() => {
    // The topic list comes from what is actually in the library, so the filter
    // never offers a heading that returns nothing.
    api
      .get<{ topics: { topic: string; count: number }[] }>("/api/library/topics")
      .then((r) => setTopics(r.topics))
      .catch(() => setTopics([]));

    api
      .get<{ languages: { language: string; count: number }[] }>("/api/library/languages")
      .then((r) => setLanguages(r.languages))
      .catch(() => setLanguages([]));
  }, []);

  // Narrowing the filter has to return to page 1: page 4 of a two-page result
  // is an empty screen that looks like an empty library.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      load(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search, difficulty, topic, language]);

  useEffect(() => {
    load(page, pageSize);
  }, [page, pageSize]);

  async function toggle(q: LibraryQuestion) {
    if (openId === q.id) {
      setOpenId(null);
      return;
    }
    setOpenId(q.id);
    if (details[q.id]) return;
    setLoadingDetail(q.id);
    try {
      const r = await api.get<{ question: LibraryQuestionDetail }>(`/api/library/${q.id}`);
      setDetails((prev) => ({ ...prev, [q.id]: r.question }));
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not load the question.");
      setOpenId(null);
    } finally {
      setLoadingDetail(null);
    }
  }

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Select or clear every question on the page currently shown, not all 50. */
  function togglePage() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }

  /**
   * One request whether it is one question or twenty -- the single Import button
   * takes the same road as the selection, so there is one behaviour to reason
   * about and one report to read.
   */
  async function runImport(ids: string[], marker: string) {
    setImporting(marker);
    try {
      const landed = await onImport(ids);
      // Only what actually landed leaves the selection, so a skipped question
      // stays ticked and visible rather than quietly disappearing.
      setSelected((prev) => {
        const next = new Set(prev);
        landed.forEach((id) => next.delete(id));
        return next;
      });
    } finally {
      setImporting(null);
    }
  }

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const pageIds = questions.map((q) => q.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const selectedHere = pageIds.filter((id) => selected.has(id)).length;

  return (
    <>
      <div className="card">
        <div className="row" style={{ flexWrap: "wrap" }}>
          <label style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
            <span>Search</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="factorial, arrays…" />
          </label>
          <label style={{ marginBottom: 0, width: 160 }}>
            <span>Difficulty</span>
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value as Difficulty | "")}>
              <option value="">All levels</option>
              <option value="EASY">Easy</option>
              <option value="MEDIUM">Medium</option>
              <option value="HARD">Hard</option>
              <option value="UNRATED">Unrated</option>
            </select>
          </label>
          <label style={{ marginBottom: 0, width: 150 }}>
            <span>Language</span>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              {/* One language today. The control still earns its place: it says
                  out loud that the library is C, which a bank about to gain
                  C++ and Python needs to have said from the start. */}
              <option value="">All languages</option>
              {languages.map((l) => (
                <option key={l.language} value={l.language}>
                  {languageLabel(l.language)} ({l.count})
                </option>
              ))}
            </select>
          </label>
          <label style={{ marginBottom: 0, width: 210 }}>
            <span>Topic</span>
            <select value={topic} onChange={(e) => setTopic(e.target.value)}>
              <option value="">All topics</option>
              {topics.map((t) => (
                <option key={t.topic} value={t.topic}>{t.topic} ({t.count})</option>
              ))}
            </select>
          </label>
          {extraFilter}
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="card muted">
          {total === 0 && (search || difficulty || topic || language)
            ? "No question matches that filter."
            : "Nothing shared yet. Faculty can share a question from its edit page."}
        </div>
      ) : (
        <>
          <div className="between" style={{ marginBottom: 8 }}>
            <label className="row" style={{ marginBottom: 0, gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={allOnPageSelected}
                onChange={togglePage}
              />
              <span style={{ display: "inline", margin: 0, fontSize: 13 }}>
                Select all on this page
              </span>
            </label>
            <span className="muted" style={{ fontSize: 13 }}>
              Showing {first}–{last} of {total}
            </span>
            <label className="row" style={{ marginBottom: 0, gap: 8, fontSize: 13 }}>
              <span style={{ display: "inline", margin: 0, fontSize: 13 }}>Per page</span>
              <select
                style={{ width: 80 }}
                value={pageSize}
                onChange={(e) => {
                  // Back to page 1: page 5 of ten-a-page does not exist at 100.
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>

          {selected.size > 0 && (
            <div className="card row" style={{ justifyContent: "space-between" }}>
              <span>
                <strong>{selected.size}</strong> question{selected.size === 1 ? "" : "s"} selected
                {/* Spelled out rather than "including some on other pages": the
                    count does not move when you search, and a teacher who has
                    just filtered to three results needs to see that the other
                    21 are still in the basket, not wonder why 24 is stuck. */}
                {selectedHere < selected.size && (
                  <span className="muted">
                    {" "}
                    — {selectedHere} on this page, {selected.size - selectedHere} hidden by the
                    current filter
                  </span>
                )}
              </span>
              <span className="row">
                {selectedHere < selected.size && (
                  <button
                    onClick={() =>
                      setSelected(new Set(pageIds.filter((id) => selected.has(id))))
                    }
                    disabled={importing !== null}
                  >
                    Keep only this page
                  </button>
                )}
                <button onClick={() => setSelected(new Set())} disabled={importing !== null}>
                  Clear
                </button>
                <button
                  className="primary"
                  disabled={importing !== null || importDisabled}
                  onClick={() => runImport([...selected], "bulk")}
                >
                  {importing === "bulk" ? "Copying…" : `${importLabel} ${selected.size} selected`}
                </button>
              </span>
            </div>
          )}

          {questions.map((q) => (
            <div className="card" key={q.id}>
              <div className="between">
                <div className="row" style={{ minWidth: 0, alignItems: "flex-start", flexWrap: "nowrap" }}>
                  <input
                    type="checkbox"
                    style={{ width: "auto", marginTop: 3 }}
                    checked={selected.has(q.id)}
                    onChange={() => toggleSelected(q.id)}
                    aria-label={`Select ${q.title}`}
                  />
                  <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{q.title}</div>
                  <div className="row" style={{ gap: 8 }}>
                    <span className={`badge level-${(q.difficulty || "unrated").toLowerCase()}`}>
                      {difficultyLabel(q.difficulty)}
                    </span>
                    {q.topic && <span className="badge topic">{q.topic}</span>}
                    <span className="muted" style={{ fontSize: 13 }}>
                      {languageLabel(q.language)} · {q._count.testCases} test cases
                      {/* Only a question shared out of a real offering names one;
                          a stock question belongs to the institution. */}
                      {q.course && ` · from ${q.course.code}`}
                    </span>
                  </div>
                  {q.description && (
                    <div className="muted" style={{ fontSize: 13, marginTop: 6, maxWidth: 620 }}>
                      {q.description.slice(0, 160)}
                      {q.description.length > 160 ? "…" : ""}
                    </div>
                  )}
                  </div>
                </div>
                <div className="row">
                  <button onClick={() => toggle(q)}>
                    {openId === q.id ? "Hide details" : "View details"}
                  </button>
                  <button
                    className="primary"
                    onClick={() => runImport([q.id], q.id)}
                    disabled={importing !== null || importDisabled}
                  >
                    {importing === q.id ? "Copying…" : importLabel}
                  </button>
                </div>
              </div>

              {openId === q.id && (
                <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                  {loadingDetail === q.id || !details[q.id] ? (
                    <div className="muted">Loading…</div>
                  ) : (
                    <QuestionPreview detail={details[q.id]} />
                  )}
                </div>
              )}
            </div>
          ))}

          <div className="row" style={{ justifyContent: "center", marginTop: 12 }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ← Previous
            </button>
            <span className="muted" style={{ fontSize: 13 }}>
              Page {page} of {pages}
            </span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              Next →
            </button>
          </div>
        </>
      )}
    </>
  );
}

/**
 * A question as the importing teacher needs to see it: exactly what a student
 * would read, then the hidden tests and starter code, which a student would not.
 */
function QuestionPreview({ detail }: { detail: LibraryQuestionDetail }) {
  const hidden = detail.testCases.filter((tc) => tc.hidden);

  return (
    <>
      <ProblemStatement
        description={detail.description}
        inputFormat={detail.inputFormat}
        outputFormat={detail.outputFormat}
        testCases={detail.testCases}
      />

      {detail.options.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="sample-label">options (correct ones marked)</div>
          <ul>
            {detail.options.map((o) => (
              <li key={o.id}>
                {o.text} {o.correct && <span className="badge pass">correct</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hidden.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="sample-label">
            {hidden.length} hidden test case{hidden.length === 1 ? "" : "s"} — students never see these
          </div>
          <table style={{ marginTop: 6 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Input</th>
                <th>Expected output</th>
              </tr>
            </thead>
            <tbody>
              {hidden.map((tc) => (
                <tr key={tc.id}>
                  <td>{tc.name}</td>
                  <td><pre className="cell-pre">{tc.stdin || "(none)"}</pre></td>
                  <td><pre className="cell-pre">{tc.expectedStdout}</pre></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail.starterCode && (
        <div style={{ marginTop: 14 }}>
          <div className="sample-label">starter code</div>
          <pre className="sample-code">{detail.starterCode}</pre>
        </div>
      )}

      <div className="muted" style={{ fontSize: 13, marginTop: 12 }}>
        {languageLabel(detail.language)} · {detail.maxMarks} marks · {detail.timeLimitMs} ms per test
        {detail.course ? ` · from ${detail.course.code}` : " · institution library"}
      </div>
    </>
  );
}
